import {useAppDispatch, useListFilterValues, useTimeConverter} from "../../../../hooks/hooks";
import {useUtil} from "../../../../util/hooks/useUtil";
import {DEFAULT_PAGE, DEFAULT_SIZE, ordersToSortArr, PageResponse, PageSearchCondition} from "../../../../types/page";
import {
    CounselorHistoryListQueryType,
    CounselorHistoryListVO,
    CounselorHistorySearchCondition
} from "../../../../types/counselor";
import {dateToString} from "../../../../util/dateUtil";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
    adminCounselingHistoryAsync,
    consultantCounselingHistoryAsync,
    consultantManagerCounselingHistoryAsync
} from "../../../../store/counselorSlice";
import {AdminRoleCategory} from "../../../../types/adminRole";
import {useSortParam} from "../../../../hooks/list/useSortParam";

export function useHistoryList() {
    const {defaultErrorMessage} = useUtil();
    const {timeConverter} = useTimeConverter()
    const dispatch = useAppDispatch();
    const [totalCounselorHistoryCount, setTotalCounselorHistoryCount] = useState<number | null>(null);
    const [counselorHistoryList, setCounselorHistoryList] =
        useState<PageResponse<CounselorHistoryListVO>>();

    const onRefresh = () => {
        getCounselorHistoryList({pageCondition}).then();
    }

    // 권한별 API 요청 처리 함수
    const getCounselingHistoryDispatcher = (role: string) => {
        switch (role) {
            case AdminRoleCategory.CONSULTANT_MANAGER:
                return consultantManagerCounselingHistoryAsync;
            case AdminRoleCategory.CONSULTANT:
                return consultantCounselingHistoryAsync;
            default:
                return adminCounselingHistoryAsync;
        }
    };

    // 상담 내역 조회 API
    const getCounselorHistoryList = useCallback(
        async (param: CounselorHistorySearchCondition, isFirstLoad?: boolean) => {
            try {
                const role = localStorage.getItem('role')
                // 권한별 조회 분기처리
                if (role) {
                    const dispatchFunction = getCounselingHistoryDispatcher(role);
                    const result: PageResponse<CounselorHistoryListVO> = await dispatch(dispatchFunction(param)).unwrap();
                    setCounselorHistoryList(result);
                    setTotalCounselorHistoryCount(result.totalElements)

                    if (isFirstLoad) {
                        setTotalCounselorHistoryCount(result.totalElements)
                    } else {
                        setCounselorHistoryList(result);
                    }
                }
            } catch (e) {
                alert(defaultErrorMessage);
            }
        },
        //eslint-disable-next-line
        []
    );

    const {filterValues, searchList, listQueryType} = useListFilterValues();
    const {toSortArray} = useSortParam();

    // 검색 파라미터
    const {
        queryFilterValue,
        queryTypeFilterValue,
        createdAtFilterValue,
        createdToFilterValue,
        desiredAtFilterValue,
        desiredToFilterValue,
        testTypeFilterValue,
        statusFilterValue,
    } = filterValues;

    const searchCounselorHistoryList = (pageSearchCondition: PageSearchCondition) => {

        const param: CounselorHistoryListQueryType = {
            query: queryFilterValue.value ? queryFilterValue.value.trim() : undefined,
            queryType: queryTypeFilterValue.value,
            createdAt: createdAtFilterValue.value ? dateToString(createdAtFilterValue.value) : undefined,
            createdTo: createdToFilterValue.value ? dateToString(createdToFilterValue.value) : undefined,
            desiredAt: desiredAtFilterValue.value ? dateToString(desiredAtFilterValue.value) : undefined,
            desiredTo: desiredToFilterValue.value ? dateToString(desiredToFilterValue.value) : undefined,
            testType: testTypeFilterValue.value ? testTypeFilterValue.value : undefined,
            status: statusFilterValue.value ? statusFilterValue.value : undefined,
        };

        return () => {
            searchList(pageSearchCondition, param)();
        }
    };

    const getCounselorHistoryListWithParam = useCallback(
        (isFirstLoad?: boolean) => {
            if (isFirstLoad) {
                getCounselorHistoryList({}, true).then();
            }

            const counselorHistoryQueryType = listQueryType as CounselorHistoryListQueryType;
            getCounselorHistoryList({
                pageCondition: {
                    page: counselorHistoryQueryType.page ? Number(counselorHistoryQueryType.page) : 0,
                    size: counselorHistoryQueryType.size ? Number(counselorHistoryQueryType.size) : 20,
                    sort: counselorHistoryQueryType.sort ? toSortArray(counselorHistoryQueryType.sort) : undefined,
                },
                query: queryFilterValue.value ? queryFilterValue.value.trim() : undefined,
                query_type: queryTypeFilterValue.value,
                created_at_from: counselorHistoryQueryType.createdAt
                    ? timeConverter.convertToFilterStartOfDay(counselorHistoryQueryType.createdAt)
                    : undefined,
                created_at_to: counselorHistoryQueryType.createdTo
                    ? timeConverter.convertToFilterStartOfDay(counselorHistoryQueryType.createdTo)
                    : undefined,
                desired_at_from: counselorHistoryQueryType.desiredAt
                    ? timeConverter.convertToFilterStartOfDay(counselorHistoryQueryType.desiredAt)
                    : undefined,
                desired_at_to: counselorHistoryQueryType.desiredTo
                    ? timeConverter.convertToFilterStartOfDay(counselorHistoryQueryType.desiredTo)
                    : undefined,
                testType: counselorHistoryQueryType.testType,
                status: counselorHistoryQueryType.status,
            }).then();

            //eslint-disable-next-line
        }, [listQueryType])

    useEffect(() => {
        if (listQueryType) {
            getCounselorHistoryListWithParam();
        }
    }, [listQueryType, getCounselorHistoryListWithParam]);


    // page condition
    const pageCondition = useMemo(
        () => ({
            page: counselorHistoryList?.number ?? DEFAULT_PAGE,
            size: counselorHistoryList?.size ?? DEFAULT_SIZE,
            sort: ordersToSortArr(counselorHistoryList?.orders),
        }),
        [counselorHistoryList]
    );
    return {
        counselorHistoryList,
        pageCondition,
        totalCounselorHistoryCount,
        searchCounselorHistoryList,
        getCounselorHistoryList,
        onRefresh
    }

}